import React, { useState } from "react";
import {
  TouchableOpacity,
  StyleSheet,
  TextInput,
  View,
  Modal,
  Text,
} from "react-native";
import { Button } from "react-native-paper";
import { useDispatch, useSelector } from "react-redux";
import {
  filterTrackerOrders as filterChinaOrders,
  resetSearch as resetChinaSearch,
} from "../../../redux/reducers/chinaTrackerReducer";
import { useTranslation } from "../../../utils/useTranslation";

interface Props {
  itemsList: Array<string | number>;
  filterCategory: string | number;
  setFilterCategory: (category: string | number) => void;
  tableHeaders: any; // Add tableHeaders prop if it's not global
  trackerType: string;
  expandProductionLine: boolean;
  setExpandProductionLine: any;
  selectedCategory?: any;
}

export const ChinaTrackerProductionLineList: React.FC<Props> = ({
  itemsList,
  filterCategory,
  setFilterCategory,
  trackerType,
  expandProductionLine,
  setExpandProductionLine,
  selectedCategory,
}) => {
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: "",
    to: "",
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const dispatch = useDispatch<any>();
  const language = useSelector((state: any) => state.configurations.language);
  const { t } = useTranslation();

  const handleDateChange = (key: "from" | "to", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilter = (filter: string | number) => {
    setFilterCategory(filter);
  };

  const validateAndApply = () => {
    if (!dateRange.from || !dateRange.to) {
      setErrorMessage("Both 'From' and 'To' dates are required.");
      return;
    }

    handleCustomDateApply(dateRange);
    setIsModalVisible(false);
  };

  const handleCustomDateApply = (dateRange: any) => {
    if (dateRange.from && dateRange.to) {
      const customFilter = `From: ${dateRange.from} To: ${dateRange.to}`;
      applyFilter(customFilter);
    }
    setIsModalVisible(false);
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};
    filters = {
      dateRange: dateRange,
    };
  };

  const handleBadgeClick = (item: string | number) => {
    const today = new Date();
    let filters: { status?: string; dateRange?: { from: string; to: string } } =
      {};

    if (item === "Custom Date Range") {
      setIsModalVisible(true); // Open modal for custom date selection
      return;
    } else if (item === "Last 3 months") {
      const threeMonthsAgo = new Date(
        today.getFullYear(),
        today.getMonth() - 3,
        today.getDate()
      );
      setDateRange({
        from: threeMonthsAgo.toISOString().split("T")[0],
        to: today.toISOString().split("T")[0],
      });
      filters = {
        dateRange: {
          from: threeMonthsAgo.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        },
      };
    } else {
      filters = { status: item };
    }

    applyFilter(item); // Update the selected badge visually
    if (item === "All" || item === t("all")) {
      dispatch(
        resetChinaSearch({
          productionLine: item,
          item: selectedCategory,
          language: language,
        })
      );
    } else {
      dispatch(
        filterChinaOrders({
          productionLine: item,
          item: selectedCategory,
          language: language,
        })
      );
    }

    setExpandProductionLine(false);
  };

  return (
    <View style={styles.badgesRow}>
      {/* Badge Row */}
      {expandProductionLine ? (
        <>
          {itemsList.map((category, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.filterButton,
                filterCategory === category && styles.activeFilterButton,
              ]}
              onPress={() => {
                handleBadgeClick(category);
              }}
            >
              <Text
                style={
                  filterCategory === category
                    ? styles.activeFilterText
                    : styles.filterText
                }
              >
                {category}
              </Text>
            </TouchableOpacity>
          ))}
        </>
      ) : (
        <TouchableOpacity
          key={"collapsed"}
          style={[styles.filterButton, styles.activeFilterButton]}
          onPress={() => {
            setExpandProductionLine(true);
          }}
        >
          <Text style={styles.activeFilterText}>
            {filterCategory !== undefined && filterCategory !== null
              ? String(filterCategory)
              : "All"}
          </Text>
        </TouchableOpacity>
      )}

      {/* Custom Date Range Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Custom Date Range</Text>

            {/* From Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select From Date"
              value={dateRange.from}
              onChangeText={(value) => handleDateChange("from", value)}
              onFocus={(e) => (e.currentTarget.type = "date")} // Show date picker on focus
            />

            {/* To Date Input */}
            <TextInput
              style={styles.dateInput}
              placeholder="Select To Date"
              value={dateRange.to}
              onChangeText={(value) => handleDateChange("to", value)}
              onFocus={(e) => (e.currentTarget.type = "date")} // Show date picker on focus
            />

            {/* Error Message */}
            {errorMessage ? (
              <Text style={styles.errorMessage}>{errorMessage}</Text>
            ) : null}

            {/* Modal Buttons */}
            <View style={styles.modalActions}>
              <Button
                mode="outlined"
                onPress={() => setIsModalVisible(false)}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                mode="contained"
                onPress={validateAndApply}
                style={styles.modalButton}
              >
                Apply
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 10,
  },
  badgesRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  badgeStyle: {
    marginHorizontal: 4,
    fontSize: 12,
    paddingHorizontal: 10,
  },
  inActiveBadge: {
    color: "black",
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  },
  activeBadge: {
    color: "white",
    backgroundColor: "rgb(47, 149, 220)",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "white",
    width: "90%",
    borderRadius: 8,
    padding: 16,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 16,
  },
  dateInput: {
    width: "100%",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginBottom: 12,
    fontSize: 14,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  modalButton: {
    flex: 1,
    marginHorizontal: 8,
  },
  errorMessage: {
    color: "red",
    marginBottom: 10,
    textAlign: "center",
  },
  filterMainContainer: {
    width: "100%",
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  filterButton: {
    paddingVertical: 8,
    paddingHorizontal: 15,
    backgroundColor: "#E0E0E0",
    borderRadius: 20,
    marginRight: 10,
  },
  activeFilterButton: {
    backgroundColor: "#1E88E5",
  },
  filterText: {
    fontSize: 14,
  },
  activeFilterText: {
    color: "#fff",
    fontSize: 14,
  },
});
